import {ExtractionJobData, JobUpload, PageProps, PersistedExtractionJob, SubtitleFormat, TranslationSummary} from "@/types";
import {Head, Link, router} from "@inertiajs/react";
import {faMessage, faPenToSquare as faPen, faTrashCan} from "@fortawesome/free-regular-svg-icons";
import {
    faArrowUpFromBracket,
    faHistory,
    faPlus,
    faRotate,
    faRotateLeft,
    faRotateRight,
    faCloudArrowDown,
    faXmark
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useEffect, useMemo, useRef, useState} from "react";
import PrimaryButton from "@/Components/PrimaryButton";
import SecondaryButton from "@/Components/SecondaryButton";
import MediaFileInput from "@/Components/MediaFileInput";
import {computeStackOffsets, findActiveSubtitleIndices, findClosestSubtitleIndex, secondsToMs} from "@/utils/subtitleUtils";
import SubtitleList from "@/Pages/Subtitle/Partials/SubtitleList";
import TranslateModal from "@/Pages/Dashboard/Translate/TranslateModal";
import SearchDropdown, {SearchPattern} from "@/Pages/Subtitle/Partials/SearchDropdown";
import {getLanguageName} from "@/utils/textFormatting";
import moment, {Moment} from "moment";
import {Tooltip} from "flowbite-react";
import EditNameModal from "@/Pages/Subtitle/Partials/EditNameModal";
import axios from "axios";
import useHistory from "@/hooks/useHistory";
import Dropdown from "@/Components/Dropdown";
import {useDebouncedCallback} from 'use-debounce';
import toast, {Toaster} from "react-hot-toast";
import {TranslationMethod} from "@/types/enums";
import FeedbackModal from "@/Pages/Dashboard/Feedback/FeedbackModal";
import TranslateIcon from "@/Components/Icons/TranslateIcon";
import ExtractionJobDetailsModal from "@/Pages/Subtitle/Partials/ExtractionJobDetailsModal";
import ExportModal from "@/Pages/Subtitle/Partials/ExportModal";
import DeleteConfirmationModal, {DeleteOption} from "@/Pages/Subtitle/Partials/DeleteConfirmationModal";
import {useConfirm} from "@/Components/ConfirmDialog";
import AiIcon from "@/Components/Icons/AiIcon";
import {
    bulkDownloadSubtitles,
    deleteSubtitle,
    downloadSubtitles,
    getSubtitleData,
    updateSubtitleData,
    updateSubtitleDataOnUnload,
} from "@/services/subtitleService";
import RemoteVideoLoader from "@/Pages/Subtitle/Partials/RemoteVideoLoader";
import BackButton from "@/Pages/Subtitle/Partials/BackButton";
import TimelineView from "@/Pages/Subtitle/Partials/TimelineView";
import VideoPlayer, {SUBTITLE_MOUNT_LEAD_TIME, VideoPlayerRef} from "@/Pages/Subtitle/Partials/VideoPlayer";
import {getTranslationJobStatus} from "@/services/translate/AI/AiTranslateService";
import UserProfileDropdown from "@/Components/UserProfileDropdown";
import LogoIcon from "@/Components/Icons/LogoIcon";
import mediaFileCacheService from "@/services/MediaFileCacheService";
import {Subtitle, SubtitleStyle} from "@/models/Subtitle";
import SubtitleStylePanel from "@/Pages/Subtitle/Partials/SubtitleStylePanel";
import Spinner from "@/Components/Spinner";
import {isEqual} from "@/utils/objectUtils";
import {SubtitleData, SubtitleMetadata} from "@/models/SubtitleData";
import {initializeSubtitleFonts} from "@/services/subtitleFontService";
import {preloadFonts} from "@/services/googleFontsService";
import MaskingModal from "@/Pages/Subtitle/Partials/MaskingModal";
import TaskPanel from "@/Pages/Subtitle/Partials/TaskPanel";
import {triggerDownload} from "@/utils/fileUtils";
import {downloadOutput} from "@/services/subtitleBurnInJobManager";
import useSessionToast from "@/utils/useSessionToast";
import usePaymentFlash from "@/hooks/usePaymentFlash";
import SplitLinesModal from "@/Pages/Subtitle/Partials/SplitLinesModal";
import useBreakpoint from "@/hooks/useBreakpoint";

type EditSubtitleProps = {
    job: ExtractionJobData,
    metadata: Record<string, any>,
    languages: Record<number, string>,
    currentLanguage: string,
    isAiTranslated: boolean,
    lastModified: string | null
    translate?: boolean
}

type VideoDimensions = {
    width?: number;
    height?: number;
}

export default function ({
    auth,
    job,
    metadata,
    lastModified,
    translate,
    languages,
    currentLanguage,
    isAiTranslated,
    sessionFlash
}: PageProps<EditSubtitleProps>) {
    const [currentItemIdx, setCurrentItemIdx] = useState<number | null>(0);
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [mediaContainsVideo, setMediaContainsVideo] = useState<boolean>(false);
    const [mediaFileRestored, setMediaFileRestored] = useState<boolean>(false);
    const [savedHistoryLength, setSavedHistoryLength] = useState<number>(0);
    const [showTranslateModal, setShowTranslateModal] = useState<boolean>(false);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
    const [showEditNameModal, setShowEditNameModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [currentMediaTime, setCurrentMediaTime] = useState<number>(0);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isTimelineCollapsed, setIsTimelineCollapsed] = useState<boolean>(false);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0);
    const [lastModifiedAt, setLastModifiedAt] = useState<Moment | null>(null);
    const [videoLoadingProgress, setVideoLoadingProgress] = useState<number>(0);
    const [videoDimensions, setVideoDimensions] = useState<VideoDimensions>({});
    const [isLoadingSubtitles, setIsLoadingSubtitles] = useState<boolean>(true);
    const videoPlayerRef = useRef<VideoPlayerRef>(null);
    const shouldScrollToListRef = useRef<boolean>(true);
    const hasInitializedFonts = useRef<boolean>(false);

    const mediaCacheKey = job.upload_id ?? job.job_name;

    const pendingLanguages = useMemo(
        () => job.translation_jobs.filter(j => j.status === 'running')
            .map(j => j.language),
        [job]
    );

    const languageCodes = useMemo(() =>
        Object.entries(languages).map(([_, value]) => value).filter(
            lang => !pendingLanguages.includes(lang)
        ),
        [languages, pendingLanguages]
    );

    useSessionToast(sessionFlash);
    usePaymentFlash(sessionFlash);

    const confirm = useConfirm();
    const {items, setItems, updateItems, addItem, undo, redo, historyLength} = useHistory<Subtitle>([
        'text',
        'startTimeMs',
        'endTimeMs',
        'style.color',
        'style.backgroundColor',
        'style.shadowDistance',
        'style.shadowColor',
        'style.centerX',
        'style.centerY',
    ]);
    const isPendingSave = historyLength !== savedHistoryLength;
    const [showStylePanel, setShowStylePanel] = useState<boolean>(false);
    const [availableFonts, setAvailableFonts] = useState<string[]>([]);
    const [showMaskingModal, setShowMaskingModal] = useState<boolean>(false);
    const [lastEditSource, setLastEditSource] = useState<'manual' | 'programmatic' | null>(null);
    const [searchPattern, setSearchPattern] = useState<SearchPattern>(null);
    const [isLoadingRemoteVideo, setIsLoadingRemoteVideo] = useState<boolean>(false);
    const [isMediaInputDismissed, setIsMediaInputDismissed] = useState<boolean>(false);
    const [showSplitLinesModal, setShowSplitLinesModal] = useState<boolean>(false);
    const haveOriginalBounds = useMemo<boolean>(() =>{
        return (job.method === 'ocr' && !Object.keys(metadata).includes('subtitle_codec'))
            || 'containsBounds' in metadata;
    }, [job]);



    const isOriginalLanguage = useMemo<boolean>(() => {
        return languages[0] === currentLanguage;
    }, [languages, currentLanguage]);

    const allHaveSameStyle = useMemo(() => {
        const currentStyle = items[0]?.style;
        return items.every(item => isEqual(item.style, currentStyle));
    }, [items]);

    const hasCustomStyles = useMemo(() => {
        return items.some(item => item.style && Object.keys(item.style).length > 2); // exclude default style
    }, [items]);

    const activeIndicesKey = findActiveSubtitleIndices(items, currentMediaTime, SUBTITLE_MOUNT_LEAD_TIME).join(',');
    const activeSubtitleItems = useMemo(
        () => activeIndicesKey === '' ? [] : activeIndicesKey.split(',').map(i => items[Number(i)]),
        [items, activeIndicesKey]
    );

    const subtitleStackOffsets = useMemo(
        () => computeStackOffsets(items, videoDimensions.height ?? 0),
        [items, videoDimensions.height]
    );

    const handleTimeUpdate = (time: number) => {
        setCurrentMediaTime(time);
    };

    const findPrevActiveIndex = (items: Subtitle[], from: number) => {
        for (let i = from - 1; i >= 0; i--) {
            if (!items[i].deleted) return i;
        }
        return -1;
    };

    const handleDeleteItem = (index: number) => {
        updateItems([{index, item: new Subtitle({...items[index], deleted: true})}])
        setCurrentItemIdx(findPrevActiveIndex(items, index));
        shouldScrollToListRef.current = true;
    }

    const handlePlayStateChange = (playing: boolean) => {
        setIsPlaying(playing);
    };

    useEffect(() => {
        setIsLoadingSubtitles(true);

        getSubtitleData(job.id, currentLanguage)
            .then(data => {
                hasInitializedFonts.current = false;
                setAvailableFonts([]);
                setItems(data.subtitles);
                shouldScrollToListRef.current = true;
            })
            .catch(_ => {
                toast.error('Failed to load subtitles');
            })
            .finally(() => {
                setIsLoadingSubtitles(false);
            });
    }, [currentLanguage]);

    useEffect(() => {
        if (
            mediaContainsVideo &&
            !hasInitializedFonts.current &&
            videoDimensions.height &&
            items.length
        ) {
            initializeSubtitleFonts(items, videoDimensions.height).then(result => {
                const fontsUsed = new Set(result.subtitles.map(s => s.style?.fontFamily).filter(Boolean));
                preloadFonts(Array.from(fontsUsed) as string[]);
                setAvailableFonts(result.availableFonts);
                setItems(result.subtitles);
                hasInitializedFonts.current = true;
                seekToTime(currentMediaTime || items[0]?.startTimeSeconds || 0);
            });
        }
    }, [items, mediaContainsVideo, videoDimensions.height]);

    useEffect(() => {
        setMediaContainsVideo(mediaFile != null && !!videoDimensions.height)
    }, [videoDimensions.height, mediaFile]);

    useEffect(() => {
        setLastModifiedAt(lastModified ? moment(lastModified) : null);
    }, [lastModified]);

    const buildSubtitleData = () => {
        const {width, height} = videoDimensions;
        const metadata = {videoWidth: width, videoHeight: height};
        return new SubtitleData({ subtitles: items, metadata });
    }

    const saveRevision = async () => {
        try {
            await updateSubtitleData(job.id, buildSubtitleData(), currentLanguage);
            setSavedHistoryLength(historyLength);
        } catch (e) {
            toast.error('Failed to save changes');
        }
    }
    const autoSave = useDebouncedCallback(saveRevision, 4000);

    useEffect(() => {
        const handlePageHide = () => {
            if (isPendingSave) {
                void updateSubtitleDataOnUnload(job.id, buildSubtitleData(), currentLanguage);
            }
        };

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isPendingSave) {
                event.preventDefault();
                event.returnValue = '';
                autoSave.cancel();
                void saveRevision();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [isPendingSave, items]);

    useEffect(() => {
        if (isPendingSave) {
            autoSave();
            setLastModifiedAt(moment());
        }
    }, [historyLength])

    useEffect(() => {
        mediaFileCacheService.restoreFile(mediaCacheKey).then(file => {
            if (file) {
                setMediaFileRestored(true);
                setMediaFile(file);
            }
        })
    }, []);

    useEffect(() => {
        if (mediaFile) {
            setVideoLoadingProgress(0);
            videoPlayerRef.current?.seekTo(currentMediaTime);
        } else {
            setMediaFileRestored(false);
            setShowStylePanel(false);
        }
    }, [mediaFile]);

    useEffect(() => {
        if (translate) {
            setShowTranslateModal(true);
            router.replace({
                url: route('subtitle.edit', job.id),
                props: (props) => ({...props, translate: false}),
                preserveState: true
            });
        }
    }, [translate]);

    useEffect(() => {
        if(showExportModal) {
            // ensure font styles are initialized before exporting
            void saveRevision();
            autoSave.cancel();
        }
    }, [showExportModal]);

    const clearMedia = async (requireConfirm: boolean = false) => {
        if (!requireConfirm || await confirm('Are you sure you want to clear the media preview?')) {
            setMediaFile(null);
            setVideoDimensions({});
            setVideoLoadingProgress(0);
            mediaFileCacheService.clearFile(mediaCacheKey);
            hasInitializedFonts.current = false;
        }
    }

    const handleVideoDimensionsChange = ({width, height}: { width: number, height: number }) => {
        if (mediaFile) {
            setVideoDimensions(prev => ({...prev, height, width}));
        }
    }

    const handleMediaError = () => {
        if (!mediaFileRestored) {
            toast.error('Unable to play this media file. Please try a different format.');
        }
        void clearMedia()
    }

    useEffect(() => {
        const bestIdx = findClosestSubtitleIndex(items, currentMediaTime);
        if (bestIdx !== currentItemIdx) {
            shouldScrollToListRef.current = true;
            setCurrentItemIdx(bestIdx);
        }
    }, [currentMediaTime, isPlaying]);

    const setTimeFromSubtitle = (index: number) => {
        if (items[index]) {
            if (mediaFile) {
                videoPlayerRef.current?.seekTo(items[index].startTimeSeconds);
            } else {
                setCurrentMediaTime(items[index].startTimeSeconds);
            }
        }
    }

    const seekToTime = (time: number) => {
        if (mediaFile) {
            videoPlayerRef.current?.seekTo(time);
        } else {
            setCurrentItemIdx(prev => prev || 0)
            setCurrentMediaTime(time);
        }
    }

    const updateSingleItem = (index: number, updatedItem: Subtitle) => {
        updateItems([{index, item: updatedItem}]);
    }

    const addNewItem = (prev: Subtitle, next?: Subtitle) => {
        const startTimeSeconds = prev.endTimeSeconds;
        const endTimeSeconds = next && next.startTimeSeconds > startTimeSeconds
            ? next.startTimeSeconds
            : startTimeSeconds + 0.1;
        const newItem: Subtitle = new Subtitle({
            id: Date.now().toString(),
            text: '',
            startTimeMs: secondsToMs(startTimeSeconds),
            endTimeMs: secondsToMs(endTimeSeconds)
        })
        addItem(newItem);
    }

    const updateName = (name: string) => {
        const url = route('subtitle.rename', job.id);
        axios.post(url, {name}).then(() => {
            router.reload();
        }).catch(console.error);
    }

    const restoreOriginalContent = async () => {
        const warning = `Are you sure you want to restore the original content? This will overwrite your current changes.`
        if (!await confirm(warning)) {
            return;
        }
        try {
            autoSave.cancel();
            setSavedHistoryLength(historyLength);
            const url = route('subtitle.restoreContent', job.id);
            await axios.post(url, {language: currentLanguage});
            document.location.reload();
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to restore original content';
            toast.error(msg);
        }
    }

    const viewLanguage = async (language: string) => {
        if (isPendingSave) {
            await saveRevision();
            autoSave.cancel();
        }
        const data: any = {language}
        router.reload({data, replace: true});
    }

    const getPendingTranslationStatus = async (language: string) => {
        try {
            const status = await getTranslationJobStatus(job.id, language)
            if (status === 'failed') {
                router.reload({ onSuccess: () =>
                    toast.error(`${getLanguageName(language)} translation failed`)
                })
            } else if (status === 'completed') {
                await viewLanguage(language);
            }  else {
                toast('Translation in progress');
            }
        } catch (e) {
            toast.error('Failed to get translation status');
        }
    }

    const download = async (
        format: SubtitleFormat,
        languages: string[],
        options: Record<any, any>
    ) => {
        if (!languages.length) {
            languages = [currentLanguage];
        }

        try {
            if (languages.length < 2) {
                await downloadSubtitles(
                    job,
                    languages[0] || null,
                    format,
                    options
                )
            } else {
                await bulkDownloadSubtitles(
                    [job.id],
                    languages,
                    format,
                    options
                )
            }
        } catch (err: any) {
            const msg = err.message || 'Failed to download subtitles';
            toast.error(msg);
        }
    }

    const deleteSelf = () => {
        setShowDeleteModal(true);
    }

    const handleDeleteConfirm = async (option: DeleteOption) => {
        try {
            const onFinish = () => toast.success('Subtitles deleted')
            if (option === 'all') {
                await deleteSubtitle(job);
                mediaFileCacheService.clearFile(mediaCacheKey);
                router.visit(route('dashboard'), {replace: true, onFinish});
            } else {
                await deleteSubtitle(job, currentLanguage);
                const remainingLanguages = languageCodes.filter(lang => lang !== currentLanguage);
                if (remainingLanguages.length > 0) {
                    toast.success(`${getLanguageName(currentLanguage)} subtitles deleted`);
                    viewLanguage(remainingLanguages[0]);
                } else {
                    router.visit(route('dashboard'), {
                        replace: true,
                        onFinish
                    });
                }
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to delete subtitles';
            toast.error(msg);
        }
    }

    const onTranslateComplete = (
        summary: TranslationSummary,
        method: TranslationMethod
    ) => {
        if (!summary.hasFailures) {
            setShowTranslateModal(false);
            const languages = summary.results[0].successful.map(s => s.language);
            if (languages.length === 1) {
                void viewLanguage(languages[0]);
            } else {
                router.reload();
            }
        }
    }

    const handleStyleChange = (style: SubtitleStyle, applyToAll: boolean) => {
        if (applyToAll) {
            const updates = items.map((item, index) => ({
                index,
                item: new Subtitle({...item, style})
            }));
            updateItems(updates);
            toast.success('Style applied to all subtitles');
        } else if (currentItemIdx !== null) {
            const updatedItem = new Subtitle({
                ...items[currentItemIdx],
                style
            });
            updateSingleItem(currentItemIdx, updatedItem);
        }
        setLastEditSource('manual');
    }

    const updateSubtitleStyleById = (id: string, styleUpdates: Partial<SubtitleStyle>) => {
        const index = items.findIndex(item => item.id === id);
        if (index === -1) return;

        const currentItem = items[index];
        if (!currentItem || currentItem.deleted) return;

        const updatedItem = new Subtitle({
            ...currentItem,
            style: {
                ...currentItem.style,
                ...styleUpdates
            }
        });

        updateSingleItem(index, updatedItem);
    };

    const handleAutoMask = () => {
        setShowMaskingModal(true);
    };

    const handleMaskingComplete = (updatedItems: Subtitle[]) => {
        updateItems(
            updatedItems.map((item, idx) => ({ index: idx, item }))
        );
        setShowMaskingModal(false);
        setLastEditSource('programmatic');
        toast.success('Masking styles applied successfully!');
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Head title="Edit Subtitles"/>

            <FeedbackModal
                onClose={() => setShowFeedbackModal(false)}
                onSubmitted={() => {
                    setShowFeedbackModal(false);
                    toast.success('Feedback submitted');
                }}
                job={showFeedbackModal ? job : null}
            />
            {
                videoDimensions.height && (
                    <MaskingModal
                        show={showMaskingModal}
                        jobId={job.id}
                        items={items}
                        availableFonts={availableFonts}
                        videoWidth={videoDimensions.width!}
                        videoHeight={videoDimensions.height}
                        isOriginalLanguage={isOriginalLanguage}
                        onComplete={handleMaskingComplete}
                        onClose={() => setShowMaskingModal(false)}
                    />
                )
            }
            <TranslateModal
                bulk={false}
                currentLanguage={currentLanguage}
                isFreeTrial={auth.user.is_trial}
                show={showTranslateModal}
                translatedLanguages={[...languageCodes, ...pendingLanguages]}
                onTranslateComplete={onTranslateComplete}
                job={job}
                subtitles={items}
                onClose={(inProgressLanguage) => {
                    setShowTranslateModal(false);
                    if (inProgressLanguage) {
                        router.reload({ only: ['job'] });
                    }
                }}
            />
            <ExtractionJobDetailsModal
                job={job}
                metadata={metadata}
                show={showDetailsModal}
                lastModifiedAt={lastModifiedAt}
                onClose={() => setShowDetailsModal(false)}
            />
            <EditNameModal
                show={showEditNameModal}
                onClose={() => setShowEditNameModal(false)}
                name={job.file_name}
                onUpdated={(name) => {
                    updateName(name)
                    setShowEditNameModal(false)
                }}
            />
            <ExportModal
                onBurnInStart={() => {
                    router.reload({ only: ['job'] });
                }}
                show={showExportModal}
                onClose={() => setShowExportModal(false)}
                onDownload={download}
                availableLanguages={languages}
                defaultLanguage={currentLanguage}
                isBulk={false}
                hasStyles={hasCustomStyles}
                initialUpload={job.upload}
                videoPlayerRef={videoPlayerRef?.current?.videoDimensions ? videoPlayerRef.current : null}
                job={job}
                isFreeTrial={auth.user.is_trial}
                onUploadComplete={() => router.reload({ only: ['job'] })}
            />
            <DeleteConfirmationModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                currentLanguage={currentLanguage}
                availableLanguages={languageCodes}
            />
            <SplitLinesModal
                show={showSplitLinesModal}
                onClose={() => setShowSplitLinesModal(false)}
                items={items}
                onApply={(updates) => {
                    if(updates.length) {
                        updateItems(updates);
                    }
                    setShowSplitLinesModal(false);
                    toast.success(`Split ${updates.length} subtitles`);
                }}
            />

            <header
                className="bg-white dark:bg-gray-800 shadow border-b border-transparent dark:border-gray-700 relative z-50">
                <div className="lg:hidden border-b border-gray-200 dark:border-gray-700 py-3 px-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0 flex-1">
                            <BackButton folderId={job.folder_id}/>
                            <h2 className="font-semibold text-base text-gray-800 dark:text-gray-100 leading-tight truncate  mr-4"
                              >
                                {job.file_name}
                            </h2>
                        </div>
                        <UserProfileDropdown user={auth.user} variant="icon"/>
                    </div>
                </div>
                <div className="lg:container mx-auto py-2 lg:py-4 px-4 sm:px-6 lg:px-2">
                    <div className="flex lg:items-center flex-col lg:flex-row">
                        <div className="hidden lg:flex flex-1 min-w-0 items-center">
                            <Link href={job.folder_id ? route('dashboard.folder', job.folder_id) : route('dashboard')}>
                                <LogoIcon tooltip="Dashboard"/>

                            </Link>
                            <div className="flex-1 min-w-0 ml-1.5 border-l border-gray-100 dark:border-gray-700 pl-5">
                                <h2
                                    className="font-semibold  xl:max-w-[75%] text-base text-gray-800 dark:text-gray-100 leading-tight truncate mr-4"
                                >
                                    <span onClick={() => setShowEditNameModal(true)}
                                          className="cursor-pointer hover:text-black dark:hover:text-white hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:rounded px-1 -mx-1">
                                        {job.file_name}
                                    </span>
                                </h2>
                                <div>
                                    <Tooltip
                                        placement={"right"}
                                        theme={{target: `inline-flex`}}
                                        className={`${languageCodes[0] === currentLanguage ? 'hidden' : ''} p-2 py-1 text-xs`}
                                        content={`${isAiTranslated ? 'Translated by AI' : 'Translated by Google'}`
                                        }>
                                        <span
                                            className={`bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-medium px-2 py-0.5 rounded items-center inline-flex ${isAiTranslated ? 'mt-1' : ''}`}>
                                            {isAiTranslated &&
                                                <AiIcon className="!h-3 !w-3 text-orange-400 inline-block mr-1"/>}
                                            <span>{getLanguageName(currentLanguage)}</span>
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                        <div
                            className="flex items-center md:flex-row justify-start md:justify-between">
                            <span>{isPendingSave && (
                                <span
                                    className="text-gray-400 dark:text-gray-500 hidden lg:inline text-sm mr-5"><FontAwesomeIcon
                                    icon={faRotate}
                                    className={`mr-1`}
                                /> Saving...</span>
                            )}</span>
                            <div className="flex flex-1 [&_button]:max-lg:border-transparent dark:[&_button]:max-lg:border-transparent dark:[&_button:not([title='Export'])]:max-lg:bg-transparent">
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <SecondaryButton className=" !px-2  mr-2 me-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                 strokeWidth={1.5} stroke="currentColor" className="size-5">
                                                <path strokeLinecap="round" strokeLinejoin="round"
                                                      d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"/>
                                            </svg>

                                        </SecondaryButton>
                                    </Dropdown.Trigger>
                                    <Dropdown.Content className="sm:mt-1 text-right" align={"right"}>
                                        <Dropdown.Item onClick={() => setShowEditNameModal(true)}>
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                className={`me-2`}
                                            />
                                            Rename</Dropdown.Item>
                                        <Dropdown.Item onClick={() => setShowSplitLinesModal(true)}>
                                            <svg className="me-1.5 -ms-0.5 size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor">
                                                <path fill="none" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5M8.25 7.5L12 3.75l3.75 3.75M12 3.75v5.25M8.25 16.5L12 20.25l3.75-3.75M12 20.25v-5.25"/>
                                            </svg>
                                            Split Lines
                                        </Dropdown.Item>
                                        {
                                            lastModifiedAt && (
                                                <Dropdown.Item onClick={restoreOriginalContent}>

                                                    <FontAwesomeIcon
                                                        className="text-gray-500 dark:text-gray-300 w-4 h-4 pr-2 flex self-center"
                                                        icon={faHistory}/>
                                                    Restore Original
                                                </Dropdown.Item>
                                            )
                                        }
                                        <Dropdown.Item onClick={() => setShowDetailsModal(true)}>
                                            <svg className={`me-2 -ms-0.5 size-5`}
                                                 xmlns="http://www.w3.org/2000/svg"
                                                 fill="none" viewBox="0 0 24 24"
                                                 strokeWidth="1.5" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round"
                                                      d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
                                            </svg>

                                            Details</Dropdown.Item>
                                        {
                                            job.method !== 'manual' &&
                                            <Dropdown.Item onClick={() => setShowFeedbackModal(true)}>
                                                <FontAwesomeIcon
                                                    className=" w-4 h-4 pr-2 flex self-end"
                                                    icon={faMessage}/>
                                                Give Feedback</Dropdown.Item>
                                        }
                                        {
                                            mediaFile && (
                                                <Dropdown.Item onClick={() => clearMedia(true)}>
                                                    <svg className="me-2 -ms-0.5 size-5" xmlns="http://www.w3.org/2000/svg"
                                                         fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                                                         stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                              d="M6 18 18 6M6 6l12 12"/>
                                                    </svg>
                                                    Remove preview
                                                </Dropdown.Item>
                                            )
                                        }
                                        <Dropdown.Item onClick={deleteSelf}>
                                            <FontAwesomeIcon
                                                icon={faTrashCan}
                                                className={`me-2`}
                                            />
                                            Delete</Dropdown.Item>
                                    </Dropdown.Content>
                                </Dropdown>

                                <TaskPanel
                                    extractionJob={job}
                                    translationJobs={job.translation_jobs}
                                    burnInJobs={job.burn_in_jobs}
                                    onBurnInComplete={(job, error) => {
                                        if(showExportModal) return;
                                        if(!error && !showExportModal) {
                                            downloadOutput(job.id).catch(console.error);
                                        }
                                        router.reload({ onSuccess: () => {
                                                if(error) {
                                                    if(error.message !== 'cancelled') {
                                                        toast.error('Burn-in failed: ' + error.message);
                                                    } else {
                                                        toast.success('Burn-in cancelled');
                                                    }
                                                } else {
                                                    const language = getLanguageName(job.language);
                                                    toast.success(`${language} burn-in completed`);
                                                }
                                            }})
                                    }}
                                    onTranslationComplete={(job, error) => {
                                        if(showTranslateModal) return;
                                        const language = getLanguageName(job.language);
                                        router.reload({ onSuccess: () => {
                                                if(error) {
                                                    toast.error(`${language} translation failed`);
                                                } else {
                                                    toast.success(`${language} translation completed`);
                                                }
                                            }});
                                    }}
                                />

                                {
                                    (undo || redo) && (
                                        <div className="inline-flex lg:mr-2 border max-lg:border-transparent border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                            <Tooltip theme={{target: `inline-flex`}} content="Undo" className="p-2 py-1 text-xs">
                                                <SecondaryButton
                                                    onClick={undo ? undo : undefined}
                                                    disabled={!undo}
                                                    className="!px-3  flex items-center !rounded-none !border-0 !ring-0 !transition-none focus:!ring-0 active:!ring-0 focus:!z-auto active:!z-auto"
                                                >
                                                    <FontAwesomeIcon icon={faRotateLeft} />
                                                </SecondaryButton>
                                            </Tooltip>
                                            <div className="hidden lg:block w-px bg-gray-200 dark:bg-gray-600" />
                                            <Tooltip theme={{target: `hidden md:inline-flex`}} content="Redo" className="p-2 py-1 text-xs">
                                                <SecondaryButton
                                                    onClick={redo ? redo : undefined}
                                                    disabled={!redo}
                                                    className="!px-3 !rounded-none flex items-center !border-0 !ring-0 !transition-none focus:!ring-0 active:!ring-0 focus:!z-auto active:!z-auto"
                                                >
                                                    <FontAwesomeIcon icon={faRotateRight} />
                                                </SecondaryButton>
                                            </Tooltip>
                                        </div>
                                    )
                                }

                                {haveOriginalBounds && mediaContainsVideo && (
                                    <SecondaryButton
                                        title="Auto Mask"
                                        className="inline-flex items-center !px-3 me-2"
                                        onClick={handleAutoMask}>
                                        <AiIcon className="h-4 w-4 lg:me-1.5 "/>

                                        <span className="hidden lg:inline">Auto Mask</span>
                                    </SecondaryButton>
                                )}
                                <SearchDropdown
                                    items={items}
                                    onNavigate={(itemIndex) => {
                                        setCurrentItemIdx(itemIndex);
                                        setTimeFromSubtitle(itemIndex);
                                        shouldScrollToListRef.current = true;
                                    }}
                                    onReplaceAll={(changes) => {
                                        updateItems(changes);
                                    }}
                                    onSearchChange={setSearchPattern}
                                />
                                <Dropdown>
                                    <Dropdown.Trigger>
                                        <SecondaryButton
                                            className="!px-3 me-2"
                                            onClick={e => {
                                                if (languageCodes.length <= 1 && pendingLanguages.length == 0) {
                                                    setShowTranslateModal(true)
                                                    e.stopPropagation()
                                                }
                                            }}>
                                            <TranslateIcon/>

                                            <span className="hidden md:inline">Translations</span>
                                            <svg
                                                className={`ms-2 hidden md:inline h-5 w-5 ${languageCodes.length === 1 ? 'hidden md:inline' : ''}`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd"
                                                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                      clipRule="evenodd"></path>
                                            </svg>
                                        </SecondaryButton>

                                    </Dropdown.Trigger>
                                    <Dropdown.Content className="sm:mt-1 min-w-60" align={"left"}>
                                        {
                                            languageCodes.map((lang, idx) => (
                                                <Dropdown.Item key={idx} onClick={() => viewLanguage(lang)}
                                                               className={`${currentLanguage === lang ? 'font-bold' : ''}`}>
                                              <span className={`flex w-full justify-between`}>
                                                  {getLanguageName(lang)}
                                                  {idx === 0 && <span
                                                      className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">Original</span>}
                                              </span>
                                                </Dropdown.Item>
                                            ))
                                        }
                                        {
                                            pendingLanguages.map((lang, idx) => (
                                                <Dropdown.Item
                                                    onClick={() => getPendingTranslationStatus(lang)}>
                                              <span className={`flex w-full justify-between`}>
                                                  {getLanguageName(lang)}
                                                  <span
                                                      className="bg-orange-100 self-start text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded">Translating...</span>
                                              </span>
                                                </Dropdown.Item>
                                            ))
                                        }
                                        <Dropdown.Item
                                            className="border-t hover:bg-white dark:hover:bg-gray-800 dark:border-gray-700"
                                            onClick={() => setShowTranslateModal(true)}>
                                            <div
                                                className="flex w-full px-3 py-2 mt-1 items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg">
                                                <FontAwesomeIcon
                                                    icon={faPlus}
                                                    className={`me-2`}
                                                /><span className="">Add Language</span>
                                            </div>
                                        </Dropdown.Item>
                                    </Dropdown.Content>
                                </Dropdown>

                                <div className="lg:hidden flex-1" />
                                <PrimaryButton
                                    title="Export"
                                    className="me-2"
                                    onClick={() => setShowExportModal(true)}
                                ><FontAwesomeIcon
                                    icon={faArrowUpFromBracket}
                                    className={`me-2 w-3 h-3`}
                                /><span className="inline me-1">Export</span>
                                </PrimaryButton>


                                <div className="hidden lg:flex items-center">
                                    <UserProfileDropdown user={auth.user} variant="icon"/>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex grow min-h-0">
                <div className="grow min-h-0">
                    <div className="lg:container mx-auto flex flex-col h-full">
                        <div className="flex flex-col-reverse lg:flex-row flex-1 min-h-0 relative">

                            <div className="lg:w-2/4 flex-1 min-h-0 relative">
                                <SubtitleList
                                    addItem={addNewItem}
                                    onSetCurrentItemIdx={idx => {
                                        setCurrentItemIdx(idx);
                                        setTimeFromSubtitle(idx);
                                        shouldScrollToListRef.current = false;
                                    }}
                                    items={items}
                                    currentItemId={currentItemIdx !== null ? items[currentItemIdx]?.id ?? null : null}
                                    deleteItem={handleDeleteItem}
                                    className={`bg-white dark:bg-gray-800 dark:border-gray-700 h-full`}
                                    updateItem={(index, updatedItem) => {
                                        updateItems([{index, item: updatedItem}])
                                    }}
                                    shouldAutoScroll={shouldScrollToListRef.current}
                                    isLoading={isLoadingSubtitles}
                                    searchPattern={searchPattern}
                                />
                            </div>

                            <div className="lg:w-2/4 lg:h-full lg:mb-0 lg:ml-8 flex flex-col">
                                {(showStylePanel && mediaContainsVideo) && (
                                    <div className="lg:mt-4 lg:mb-5">
                                        <SubtitleStylePanel
                                            subtitle={currentItemIdx !== null ? items[currentItemIdx] : null}
                                            availableFonts={availableFonts}
                                            allHaveSameStyle={allHaveSameStyle}
                                            lastEditSource={lastEditSource}
                                            onStyleChange={handleStyleChange}
                                            onClose={() => setShowStylePanel(false)}
                                        />
                                    </div>
                                )}
                                <div className={`relative ${mediaFile ? '' : 'hidden'} subtitle-media`}>
                                    <div
                                        className={`${showStylePanel && mediaContainsVideo ? ' ' : 'lg:mt-[5vh]'} w-full lg:rounded-lg`}>
                                        <div
                                            className={videoLoadingProgress === 1 ? '' : 'absolute invisible left-[-9999px] top-0'}>
                                            <VideoPlayer
                                                ref={videoPlayerRef}
                                                mediaFile={mediaFile}
                                                cacheKey={mediaCacheKey}
                                                playbackSpeed={playbackSpeed}
                                                onTimeUpdate={handleTimeUpdate}
                                                onVideoLoadingProgress={val => setVideoLoadingProgress(val)}
                                                onVideoDimensionsChange={handleVideoDimensionsChange}
                                                onPlayStateChange={handlePlayStateChange}
                                                onTimeSeek={seekToTime}
                                                onPlaybackSpeedChange={setPlaybackSpeed}
                                                onSubtitlePositionChange={(id, centerX, centerY) => {
                                                    updateSubtitleStyleById(id, { centerX, centerY });
                                                    setLastEditSource('manual');
                                                }}
                                                onSubtitlePaddingChange={(id, paddingX, paddingY) => {
                                                    updateSubtitleStyleById(id, { paddingX, paddingY });
                                                    setLastEditSource('manual');
                                                }}
                                                currentTime={currentMediaTime}
                                                duration={job.file_duration}
                                                isPlaying={isPlaying}
                                                activeSubtitleItems={activeSubtitleItems}
                                                subtitleStackOffsets={subtitleStackOffsets}
                                                onMediaError={handleMediaError}
                                                onSubtitleClick={(id) => {
                                                    const idx = items.findIndex(item => item.id === id);
                                                    if (idx !== -1) {
                                                        setCurrentItemIdx(idx);
                                                    }
                                                    setShowStylePanel(true);
                                                }}
                                            />
                                        </div>
                                        {videoLoadingProgress < 1 && (
                                            <div
                                                className="flex items-center justify-center   lg:border border-gray-200 dark:border-gray-700 rounded-lg bg-gradient-to-br from-white/30 via-white/10 to-transparent dark:from-white/10 dark:via-transparent dark:to-transparent py-[11vh]">
                                                <div className="flex flex-col items-center">
                                                    <Spinner size={12} progress={videoLoadingProgress}/>
                                                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading
                                                        video...</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                </div>
                                {
                                    !mediaFile && !isMediaInputDismissed &&
                                    <div
                                        className="relative px-4 md:mx-0 py-2 pt-5 pb-4 md:py-0 lg:mt-[5vh] md:block border-dashed lg:border-2 border-gray-200 dark:border-gray-700 rounded-lg md:bg-gradient-to-br from-white/30 via-white/10 to-transparent dark:from-white/10 dark:via-transparent dark:to-transparent">
                                        <button
                                            onClick={() => setIsMediaInputDismissed(true)}
                                            className="lg:hidden absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                        >
                                            <FontAwesomeIcon icon={faXmark} className="w-4 h-4"/>
                                        </button>
                                        {isLoadingRemoteVideo ? (
                                            <RemoteVideoLoader
                                                jobId={job.id}
                                                cacheKey={mediaCacheKey}
                                                fileName={job.file_name}
                                                onLoaded={(file) => {
                                                    setIsLoadingRemoteVideo(false);
                                                    setMediaFile(file);
                                                }}
                                                onError={(msg) => {
                                                    setIsLoadingRemoteVideo(false);
                                                    toast.error(msg);
                                                }}
                                                onCancel={() => setIsLoadingRemoteVideo(false)}
                                            />
                                        ) : (
                                            <MediaFileInput
                                                className="w-full dropzone flex justify-center py-[1vh] md:py-[5vh] lg:py-[11vh]"
                                                button={onclick => {
                                                    return (
                                                        <div className="flex justify-center gap-2 -mt-4 md:mt-0">
                                                            <button
                                                                onClick={onclick}
                                                                className={`py-1.5 px-3 inline-block text-center rounded leading-normal text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600 hover:ring-0 hover:border-gray-200 dark:hover:border-gray-500 focus:bg-gray-200 dark:focus:bg-gray-600 focus:border-gray-200 dark:focus:border-gray-500 focus:outline-none focus:ring-0`}>
                                                                Browse file
                                                            </button>
                                                            <LoadMediaButton
                                                                upload={job.upload}
                                                                source={metadata.source}
                                                                onClick={() => setIsLoadingRemoteVideo(true)}
                                                            />
                                                        </div>
                                                    )
                                                }}
                                                icon={
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                         strokeWidth={1.5} stroke="currentColor"
                                                         className="hidden md:block size-9 mx-auto text-gray-500 dark:text-gray-400 drop-shadow-white">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                              d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"/>
                                                    </svg>
                                                }
                                                label="Add media to preview subtitles in sync"
                                                multiple={false}
                                                onFilesSelected={files => {
                                                    setMediaFile(files[0])
                                                    mediaFileCacheService.saveFile(mediaCacheKey, files[0])
                                                }}
                                                canSelectUrl={false}/>
                                        )}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <TimelineView
                items={items}
                currentTime={currentMediaTime}
                duration={job.file_duration}
                onItemUpdate={updateSingleItem}
                onTimeSeek={seekToTime}
                isPlaying={isPlaying}
                playbackSpeed={playbackSpeed}
                onItemClick={(index) => {
                    setCurrentItemIdx(index);
                    setTimeFromSubtitle(index);
                    shouldScrollToListRef.current = true;
                }}
                selectedItemIndex={currentItemIdx}
                className="flex-shrink-0"
                audioFile={mediaFile || undefined}
                isCollapsed={isTimelineCollapsed}
                onToggleCollapse={() => setIsTimelineCollapsed(!isTimelineCollapsed)}
            />

            <Toaster
                toastOptions={{className: 'dark:bg-gray-800 dark:text-white'}}
            />
        </div>
    )
}

function LoadMediaButton({upload, onClick, source }: { upload: JobUpload | null, source: string, onClick: () => void }) {
    const expired = !upload || moment(upload.expires_at).isBefore(moment());
    const isSm = useBreakpoint('(max-width: 767px)');

    const btn = (
        <button
            onClick={(e) => {
                e.stopPropagation();
                if (!expired) onClick();
            }}
            disabled={expired}
            className={`py-1.5 px-3 ${isSm && source !== 'remote' ? 'hidden md:inline-block' : ''} md:inline-block text-center rounded leading-normal border focus:outline-none focus:ring-0 ${
                expired
                    ? 'text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border-gray-100 dark:border-gray-600 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600 hover:border-gray-200 dark:hover:border-gray-500'
            }`}>

            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className={`me-1 ${expired ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-100'} size-5 inline-block align-text-bottom`}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
            </svg>
            Load media
        </button>
    );

    if (!expired) return btn;

    return (
        <Tooltip placement={"right"} theme={{target: 'inline-flex'}} className="p-2 py-1 text-xs" content="Uploaded file has expired">
            {btn}
        </Tooltip>
    );
}
